import { BaseAction, State, selectProfile, selectProfileDetails } from './index'
import {
    ProfileResponse,
    LoginRequest,
    RoleResponseState,
    ProfileRequest,
    TeamResponseState,
} from '../services/api/profile'
import { createSelector } from 'reselect'
import { IdentifyState } from 'config/analytics'
import { CampaignSubmission } from 'components/Shared/Permission/enums'

export interface CProfileState {
    uuid: string
    name: string
}
export interface TeamState {
    uuid: string
    id: string
    label: string
    service_fee_percentage: number
    type: string
    name: string
    onboarding_options?: {
        business_challenges?: string[]
        product_type?: string | null
        product_access_types?: string[]
        platform_content_types?: string[]
        industries?: string[]
    } | null
}
export interface ProfileState extends CProfileState {
    email: string
    starred?: boolean
    is_admin_user?: boolean
    is_internal_staff?: boolean
    is_registered?: boolean
    teams?: TeamState[]
    role?: RoleResponseState
    is_campaigns_empty?: boolean
    is_billing_info_ready?: boolean
}

const initialState: ProfileState = {
    email: '',
    name: '',
    starred: false,
    is_admin_user: false,
    is_internal_staff: false,
    is_registered: true,
    uuid: '',
    teams: [],
}

export enum ProfileActions {
    LOGIN_REQUEST = 'profile/LOGIN_REQUEST',
    LOGOUT_REQUEST = 'profile/LOGOUT_REQUEST',
    PROFILE_REQUEST = 'profile/PROFILE_REQUEST',
    SAVE_DETAILS = 'profile/SAVE_DETAILS',
    RESET_PASSWORD_REQUEST = 'profile/RESET_PASSWORD_REQUEST',
    UPDATE_PASSWORD_REQUEST = 'profile/UPDATE_PASSWORD_REQUEST',
    VERIFY_TOKEN_REQUEST = 'VERIFY_TOKEN_REQUEST',
    GOOGLE_AUTH_REQUEST = 'profile/GOOGLE_AUTH_REQUEST',
}

interface SaveProfileDetailsPayload {
    data: ProfileResponse | undefined
}

export const saveProfileDetails = (payload: SaveProfileDetailsPayload) => ({
    payload,
    type: ProfileActions.SAVE_DETAILS,
})

export const login = (payload: LoginRequest) => ({
    payload,
    type: ProfileActions.LOGIN_REQUEST,
})

export const logout = () => ({
    type: ProfileActions.LOGOUT_REQUEST,
})

export const getProfileDetail = (payload?: ProfileRequest) => ({
    payload,
    type: ProfileActions.PROFILE_REQUEST,
})

export interface ResetPasswordRequestPayload {
    email: string
    onSuccess?: () => void
}

export const resetPasswordRequest = (payload: ResetPasswordRequestPayload) => ({
    payload,
    type: ProfileActions.RESET_PASSWORD_REQUEST,
})

export interface BasicSignUpResetPayload {
    uid: string
    token: string
    onSuccess?: (email: string) => void
    onError?: () => void
}
export interface UpdatePasswordRequestPayload extends BasicSignUpResetPayload {
    email: string
    new_password1: string
    new_password2: string
    onSuccess?: () => void
    isSignUp?: boolean
}

export const verifyTokenRequest = (payload: BasicSignUpResetPayload) => ({
    payload,
    type: ProfileActions.VERIFY_TOKEN_REQUEST,
})

export const updatePasswordRequest = (payload: UpdatePasswordRequestPayload) => ({
    payload,
    type: ProfileActions.UPDATE_PASSWORD_REQUEST,
})

export interface GoogleAuthRequestPayload {
    temp_token: string
    redirectLink?: string | null
}

export const googleAuth = (payload: GoogleAuthRequestPayload) => ({
    payload,
    type: ProfileActions.GOOGLE_AUTH_REQUEST,
})

export const profileReducer = (state: ProfileState = initialState, action: BaseAction) => {
    switch (action.type) {
        case ProfileActions.SAVE_DETAILS: {
            const reMapTeams: TeamState[] = []
            action.payload.data.teams.forEach((team: TeamResponseState) => {
                const returnTeam: TeamState = {
                    ...team,
                    id: team.uuid,
                    label: team.name,
                    service_fee_percentage: Number(team.service_fee_percentage ?? 0),
                    type: team.type ?? '',
                }
                reMapTeams.push(returnTeam)
            })
            return Object.assign({}, state, { ...action.payload.data, teams: reMapTeams })
        }
        case ProfileActions.LOGOUT_REQUEST:
            return initialState
        default:
            return state
    }
}

export const getDetails = (state: ProfileState) => {
    return {
        email: state.email,
        name: state.name,
        canSeeInWaitingListSubmission: state.is_admin_user || state.is_internal_staff,
        canRejectInWaitingListSubmission: state.starred && state.is_internal_staff,
        canSeeNextBatchStartModal: (state.is_admin_user || state.starred) && !state.is_internal_staff,
        canSeeNextBatchDisplaySoonModal: (!state.is_admin_user || !state.starred) && !state.is_internal_staff,
        uuid: state.uuid,
    }
}
export const selectIsCampaignsEmpty = (state: State): boolean => Boolean(selectProfile(state).is_campaigns_empty)

export const selectIsExternalAdminUser = (state: State): boolean =>
    Boolean(selectProfile(state).is_admin_user && !selectIsInternalUser(state))

export const selectIsExternalUser = (state: State): boolean => Boolean(!selectIsInternalUser(state))

export const selectIsInternalUser = (state: State): boolean => Boolean(selectProfile(state).is_internal_staff)

export const selectCMUserUuid = (state: State): string => selectProfile(state).uuid

export const selectCanSeeInWaitingListSubmission = (state: State): boolean | undefined =>
    selectProfileDetails(state).canSeeInWaitingListSubmission

export const selectCanRejectInWaitingListSubmission = (state: State): boolean | undefined =>
    selectProfileDetails(state).canRejectInWaitingListSubmission
export const selectCanSeeNextBatchStartModal = (state: State): boolean | undefined =>
    selectProfileDetails(state).canSeeNextBatchStartModal
export const selectCanSeeNextBatchDisplaySoonModal = (state: State): boolean | undefined =>
    selectProfileDetails(state).canSeeNextBatchDisplaySoonModal
export const selectTeamList = (state: State) => {
    if (state.profile.teams) {
        return state.profile.teams
    } else {
        return []
    }
}
export const selectProfileRoleData = (state: State) => {
    return state.profile.role
}

export const selectProfileProperties = createSelector(
    selectIsInternalUser,
    selectCMUserUuid,
    selectTeamList,
    (isInternalUser, uuid, teamsList): IdentifyState => {
        const teams: string[] = []
        if (teamsList.length > 0) {
            teamsList.forEach((team: any) => {
                teams.push(team.id)
            })
        }
        return {
            accountTypes: isInternalUser ? 'partipost' : 'external',
            teams: teams,
            clientID: uuid,
        }
    },
)
export const selectUserPermissionCodes = createSelector(
    selectProfileRoleData,
    profileRoleData => new Set(profileRoleData?.permissions?.map(permission => permission.code) ?? []),
)
export const selectCanDirectReview = createSelector(selectUserPermissionCodes, permissionCodes =>
    permissionCodes.has(CampaignSubmission.ReviewDirectly),
)
export const selectCanIndirectReview = createSelector(selectUserPermissionCodes, permissionCodes =>
    permissionCodes.has(CampaignSubmission.ReviewIndirectly),
)
export const selectCanEditSchedulePostTime = createSelector(selectUserPermissionCodes, permissionCodes =>
    permissionCodes.has(CampaignSubmission.EditPostSchedule),
)

export const selectUserIsRegistered = (state: State) => state.profile?.is_registered
